import { useState } from 'react';

// Utils & misc
import cn from '../../../utils/cn';

// Components
import Picto from '../../atoms/Picto/Picto';

// Styles
import styles from './CalendarSidebar.module.css';

/**
 * CalendarSidebar - Left sidebar shell for calendar/timeline filters
 * @param {object} props
 * @param {React.ReactNode} props.children - Sidebar content
 * @param {boolean} [props.isCollapsed=false] - Whether the sidebar is collapsed to a rail
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebar component
 */
function CalendarSidebar({ children, isCollapsed = false, className = '' }) {
  return (
    <aside
      className={cn([
        styles.sidebar,
        isCollapsed && styles['sidebar-collapsed'],
        className,
      ])}
    >
      {children}
    </aside>
  );
}

/**
 * CalendarSidebarHeader - Top row hosting the collapse/expand toggle
 * @param {object} props
 * @param {React.ReactNode} props.children - Header content (typically the toggle)
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarHeader component
 */
export function CalendarSidebarHeader({ children, className = '' }) {
  return (
    <div className={cn([styles.header, className])}>
      {children}
    </div>
  );
}

/**
 * CalendarSidebarRail - Vertical stack shown only when the sidebar is collapsed
 * @param {object} props
 * @param {React.ReactNode} props.children - Rail content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarRail component
 */
export function CalendarSidebarRail({ children, className = '' }) {
  return (
    <div className={cn([styles.rail, className])}>
      {children}
    </div>
  );
}

/**
 * CalendarSidebarRailDivider - Thin separator in the collapsed rail
 * @param {object} props
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarRailDivider component
 */
export function CalendarSidebarRailDivider({ className = '' }) {
  return (
    <div className={cn([styles['rail-divider'], className])} />
  );
}

/**
 * CalendarSidebarRailLabel - Vertical label filling remaining rail height
 * @param {object} props
 * @param {React.ReactNode} props.children - Label text
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarRailLabel component
 */
export function CalendarSidebarRailLabel({ children, className = '' }) {
  return (
    <p className={cn([styles['rail-label'], className])}>
      {children}
    </p>
  );
}

/**
 * CalendarSidebarSections - Vertical stack of sidebar sections
 * @param {object} props
 * @param {React.ReactNode} props.children - Sections content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarSections component
 */
export function CalendarSidebarSections({ children, className = '' }) {
  return (
    <div className={cn([styles.sections, className])}>
      {children}
    </div>
  );
}

/**
 * CalendarSidebarSection - Collapsible accordion section
 * @param {object} props
 * @param {string} props.label - Section title
 * @param {React.ReactNode} [props.action] - Optional action (e.g. add button) next to chevron
 * @param {boolean} [props.defaultOpen=true] - Whether section starts open
 * @param {React.ReactNode} props.children - Section content
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarSection component
 */
export function CalendarSidebarSection({
  label,
  action = null,
  defaultOpen = true,
  children,
  className = '',
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <div className={cn([styles.section, className])}>
      <div className={styles['section-header']}>
        <button
          type="button"
          className={styles['section-toggle']}
          onClick={toggle}
          aria-expanded={isOpen}
        >
          <span className={styles['section-label']}>{label}</span>
        </button>
        {action ? (
          <span className={styles['section-actions']}>
            {action}
          </span>
        ) : null}
        <button
          type="button"
          className={styles['section-chevron-button']}
          onClick={toggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Collapse' : 'Expand'}
        >
          <Picto
            icon={isOpen ? 'chevron-up-range' : 'chevron-down-range'}
            className={styles['section-chevron']}
          />
        </button>
      </div>
      <div
        className={cn([
          styles['section-content'],
          !isOpen ? styles['section-content-collapsed'] : null,
        ])}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * CalendarSidebarField - Label + control block (e.g. select)
 * @param {object} props
 * @param {string} props.label - Field label
 * @param {React.ReactNode} props.children - Field control
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} CalendarSidebarField component
 */
export function CalendarSidebarField({ label, children, className = '' }) {
  return (
    <div className={cn([styles.field, className])}>
      <p className={styles['field-label']}>{label}</p>
      {children}
    </div>
  );
}

export default CalendarSidebar;
